
import React, { useState, useEffect } from 'react'
import axios from 'axios';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/solid";
import { baseUrl, limit, formatDate, formatMasterReportValue } from "../../comman/config";
import * as XLSX from 'xlsx';

import Loader from '../../components/Loader';
import { ItemName, itemExportName } from '../../components/ItemName';

// Production Report (mobile layout). One block per menu item, each holding the six measures
// below as Planned / Production / % Diff / Historical. Every value comes from the Raw Report
// payload (/reports/master_report) -- the same numbers the Raw Report shows in its Planned,
// Production and Historical sections, so the two screens can never drift apart.
//
// Each cell is { v: accessor, f: Raw Report field name }. Carrying the field name means the
// decimals come from formatMasterReportValue, so a cell reads exactly as it does on the Raw
// Report (2dp by default, 3dp for the ratios, whole numbers for the counts) instead of following
// a second, hand-picked rounding rule. `historical` is absent on the measures that have none.
const METRICS = [
    {
        key: 'rm_qty', label: 'RM Qty. (kgs.)',
        planned: { v: (r) => r.raw_material?.planned_rm_qty, f: 'planned_rm_qty' },        // Raw Report > Planned > RM
        production: { v: (r) => r.batch_raw_material_qty, f: 'batch_raw_material_qty' },   // Raw Report > Production > Actual RM
    },
    {
        key: 'fg_qty', label: 'FG Qty (kgs.)',
        planned: { v: (r) => r.planned_fg, f: 'planned_fg' },                              // Raw Report > Planned > FG
        production: { v: (r) => r.finishedQty, f: 'finishedQty' },                         // Raw Report > Production > FG
    },
    {
        key: 'fg_per_person', label: 'FG per person (gms.)',
        planned: { v: (r) => r.planned_fg_per_person, f: 'planned_fg_per_person' },        // Raw Report > Planned > FG / Person
        production: { v: (r) => r.material_fg_per_person, f: 'material_fg_per_person' },   // Raw Report > Production > FG / Person
        historical: { v: (r) => r.fg_per_person_gms, f: 'fg_per_person_gms' },             // Raw Report > Historical > FG / Person
    },
    {
        key: 'rm_to_fg', label: 'RM to Fg ratio',
        planned: { v: (r) => r.planned_rm_to_fg_ratio, f: 'planned_rm_to_fg_ratio' },      // Raw Report > Planned > RM to FG Ratio
        production: { v: (r) => r.current_rm_to_fg_ratio, f: 'current_rm_to_fg_ratio' },   // Raw Report > Production > RM to FG Ratio
        historical: { v: (r) => r.rm_to_fg_ratio, f: 'rm_to_fg_ratio' },                   // Raw Report > Historical > RM to FG Ratio
    },
    {
        key: 'ras', label: 'RAS',
        planned: { v: (r) => r.raw_material?.ras_qty_per_100_person, f: 'ras_qty_per_100_person' }, // Raw Report > Planned > RAS
        production: { v: (r) => r.production_ras, f: 'production_ras' },                   // Raw Report > Production > RAS
    },
    {
        key: 'count', label: 'Count',
        planned: { v: (r) => r.raw_material?.estimated_count, f: 'estimated_count' },      // Raw Report > Planned > Count
        production: { v: (r) => r.production_count, f: 'production_count' },               // Raw Report > Production > Count
        historical: { v: (r) => r.food_qty, f: 'food_qty' },                               // Raw Report > Historical > Count
    },
];

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

// Cell text, formatted the way the Raw Report formats that same field.
const show = (cell, row) => {
    if (!cell) return '';
    const value = cell.v(row);
    if (value === null || value === undefined || value === '') return '-';
    return formatMasterReportValue(value, cell.f);
};

// % Diff = (Production - Planned) / Planned. Nothing to compare against when Planned is 0.
const pctDiff = (planned, production) => {
    const p = num(planned);
    if (p === 0) return null;
    return ((num(production) - p) / p) * 100;
};

// Count shading, used for both the item's accordion bar and its Count row: green once Production
// Count reaches Planned, red when it falls short, white until production is entered at all.
// The two tints sit at the same step of their scales, so a shortfall reads as strongly as a
// met plan rather than fading into the background.
const countBg = (planned, production) => {
    const prod = num(production);
    if (prod <= 0) return '#ffffff';
    return prod >= num(planned) ? '#86efac' : '#fca5a5';
};

// The Count measure drives the bar colour, so it is looked up once rather than searched per render.
const COUNT_METRIC = METRICS.find((m) => m.key === 'count');
const countBgForRow = (row) => countBg(COUNT_METRIC.planned.v(row), COUNT_METRIC.production.v(row));

// The report reads in kgs, so it shows the row the Raw Report presents as Kgs: genuine Kgs
// (method 1), the ltr/nos -> Kgs mirror rows (also method 1) and Readymade-Nos (6). Exactly one
// row per item qualifies, so an item never appears twice. An item with no such row (older data)
// falls back to its first row rather than dropping off the report.
const KGS_METHOD_IDS = [1, 6];

const groupByItem = (rows) => {
    const groups = new Map();
    rows.forEach((row) => {
        const key = `${row.date}|${row.session}|${row.menu_item_id}`;
        const existing = groups.get(key);
        const preferred = KGS_METHOD_IDS.includes(Number(row.methodName?.id));
        if (!existing) {
            groups.set(key, { key, row, preferred });
        } else if (preferred && !existing.preferred) {
            groups.set(key, { key, row, preferred });
        }
    });
    return [...groups.values()].map((g) => g.row);
};

function List() {

    const today = new Date().toISOString().split("T")[0];
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [date, setDate] = useState(today);
    const [session, setSession] = useState(null);
    const [sessionList, setSessionList] = useState([]);
    // One item open at a time, matching Production Support and the other accordion screens.
    // null = everything collapsed, which is how the report opens.
    const [activeIndex, setActiveIndex] = useState(null);

    useEffect(() => {
        getSessionList();
        getReport(today, null);
    }, []);

    const handleClick = (index) => {
        setActiveIndex(index === activeIndex ? null : index);
    };

    const getSessionList = async () => {
        const response = await axios.get(`${baseUrl}/comman/list?table=session_master&condition=status=1`);
        setSessionList((response.data.data ?? []).map((s) => ({ value: s.id, label: s.name })));
    }

    // Args are passed explicitly by Submit -- state set in the same tick is still stale here.
    const getReport = async (d = date, s = session) => {
        setLoading(true);
        try {
            const sessionParam = s ? [s] : [];
            const response = await axios.get(`${baseUrl}/reports/master_report?page=1&limit=${limit}&fromDate=${d}&toDate=${d}` +
                `&session=${encodeURIComponent(JSON.stringify(sessionParam))}&item=${encodeURIComponent(JSON.stringify([]))}&method=${encodeURIComponent(JSON.stringify([]))}&itemFilter=in`);
            setRows(response.data.data ?? []);
            // Collapse on refetch -- the open index would otherwise point at a different item.
            setActiveIndex(null);
        } catch (e) {
            console.error('Failed to load production report', e);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }

    const items = groupByItem(rows.filter((r) => r.measurements));

    // Coupons issued is "Dish Issue Edit > Coupons sold" (meal_master.coupons_sold, surfaced as
    // pass_issued). It is a per date+session figure, not a per item one, so every row of a session
    // repeats the same number -- hence one entry per distinct session rather than per row.
    const couponSlots = [...items.reduce((acc, r) => {
        const key = `${r.date}|${r.session}`;
        if (!acc.has(key)) acc.set(key, { date: r.date, session: r.session, event: r.event, value: num(r.pass_issued) });
        return acc;
    }, new Map()).values()];
    // With more than one date+session in view the figure is a total of them all.
    const couponIssued = couponSlots.reduce((sum, s) => sum + s.value, 0);

    // Session is only worth naming on the item header when the report spans more than one.
    const multiSession = new Set(items.map((r) => r.session)).size > 1;

    const handleExport = () => {
        const header = ["Date", "Session", "Menu Item", "Measure", "Planned", "Production", "% Diff", "Historical"];
        const data = [];
        items.forEach((row) => {
            METRICS.forEach((m) => {
                const diff = pctDiff(m.planned.v(row), m.production.v(row));
                data.push([
                    formatDate(row.date),
                    row.session_name,
                    itemExportName(row),
                    m.label,
                    show(m.planned, row),
                    show(m.production, row),
                    diff === null ? '-' : `${Math.round(diff)}%`,
                    show(m.historical, row),
                ]);
            });
        });

        const worksheet = XLSX.utils.aoa_to_sheet([
            ["Coupon Issued", couponIssued],
            [],
            header,
            ...data,
        ]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Production Report");
        XLSX.writeFile(workbook, `Production_report_${formatDate(date)}${session?.label ? '_' + session.label : ''}.xlsx`);
    }

    const cell = "px-2 py-1 border border-black text-center";

    return (
        <div className="p-2 sm:p-4">
            {loading && <Loader/>}
            <div className="flex flex-wrap justify-between items-center gap-2 py-3 px-2 sm:px-6">
                <h1 className="text-xl sm:text-2xl font-bold">Production Report</h1>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleExport}
                        className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                        Export
                    </button>
                    <Link to="/">
                        <button className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Back</button>
                    </Link>
                </div>
            </div>

            {/* Filter: date (defaults to today) + session, applied on Submit. Kept deliberately small
                so the screen stays usable on a phone. */}
            <div className="flex flex-wrap items-center gap-2 px-2 sm:px-6 pb-3">
                <input
                    type="date"
                    className="p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                />
                <div className="w-40">
                    <Select
                        isClearable
                        classNamePrefix="select"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        value={session}
                        onChange={(selected) => setSession(selected)}
                        options={sessionList}
                        placeholder="Session"
                    />
                </div>
                <button
                    onClick={() => getReport(date, session)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                    Submit
                </button>
            </div>

            {/* Coupons issued, read only. It is Dish Issue Edit > Coupons sold
                (meal_master.coupons_sold), and that screen is where it is set. */}
            <div className="flex flex-wrap items-center gap-2 px-2 sm:px-6 pb-3 text-sm font-bold text-gray-900">
                <span>Coupon Issued :</span>
                <span className="ml-1">{couponIssued}</span>
                {couponSlots.length > 1 && (
                    <span className="font-normal text-xs text-gray-500">
                        (total across sessions)
                    </span>
                )}
            </div>

            <div className="px-2 sm:px-6 space-y-4">
                {items.map((row, index) => (
                    <div key={`${row.date}|${row.session}|${row.menu_item_id}`} className="shadow-md rounded-sm overflow-hidden">
                        {/* Accordion bar. Its colour is the Count rule: green once Production Count
                            reaches Planned, red when it falls short, white until production is
                            entered -- so the day reads at a glance with every item collapsed. */}
                        <div
                            onClick={() => handleClick(index)}
                            style={{ backgroundColor: countBgForRow(row) }}
                            className="flex justify-between items-center gap-2 px-2 py-2 cursor-pointer accordion-title border border-black font-bold text-gray-900">
                            <span>
                                <ItemName item={row} />
                                {multiSession && <span className="ml-1 font-normal text-xs">({row.session_name})</span>}
                            </span>
                            {index === activeIndex
                                ? <ChevronUpIcon className="w-5 h-5 shrink-0" />
                                : <ChevronDownIcon className="w-5 h-5 shrink-0" />}
                        </div>
                        {index === activeIndex && (
                        <table className="w-full text-xs sm:text-sm text-gray-900">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className={cell}></th>
                                    <th className={cell}>Planned</th>
                                    <th className={cell}>Production</th>
                                    <th className={cell}>% Diff</th>
                                    <th className={cell}>Historical</th>
                                </tr>
                            </thead>
                            <tbody>
                                {METRICS.map((m) => {
                                    const planned = m.planned.v(row);
                                    const production = m.production.v(row);
                                    const diff = pctDiff(planned, production);
                                    // Only the Count row is shaded -- green when production met the plan,
                                    // red when it fell short, white before production is entered.
                                    const bg = m.key === 'count' ? countBg(planned, production) : undefined;
                                    return (
                                        <tr key={m.key}>
                                            <th scope="row" className={`${cell} font-medium bg-gray-50`}>{m.label}</th>
                                            <td className={cell} style={bg ? { backgroundColor: bg } : undefined}>{show(m.planned, row)}</td>
                                            <td className={cell} style={bg ? { backgroundColor: bg } : undefined}>{show(m.production, row)}</td>
                                            <td className={cell}>{diff === null ? '-' : `${Math.round(diff)}%`}</td>
                                            <td className={cell}>{show(m.historical, row)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        )}
                    </div>
                ))}
                {!loading && items.length === 0 && (
                    <div className="py-6 text-center text-gray-500">No menu items for this date and session.</div>
                )}
            </div>
        </div>
  );
}

export default List;
